import type { TextKey } from "../i18n/es.ts";

// Base map shared between the start screen and the working map: CARTO
// Voyager, more legible than OSM's default Mapnik and with rivers/water
// bodies already highlighted in blue, coherent with the app's domain.
//
// CARTO's raster basemaps stopped being usable anonymously (2026-08-27) —
// every tile now comes back with an "API KEY REQUIRED" watermark without one
// (a policy change on their end, not a regression here; it's hit other
// projects the same way). The free tier (5M requests/month,
// https://carto.com/basemaps/apikey/) is enough for this app — decided to
// keep Voyager's style rather than fall back to plain OSM. Same pattern as
// VITE_GOOGLE_CLIENT_ID (driveSyncStore.ts): a real key, but NOT a secret —
// CARTO's own docs show it appended directly to the client-side tile URL, so
// it's a normal Vite build-time env var, not something that needs a backend
// proxy. See .env.local.example for local dev and README.md for the
// Cloudflare Pages setup. Falls back to the same (now watermarked) URL when
// unset, rather than hiding the "Calles" option entirely — no worse than the
// status quo before a key is configured.
const CARTO_API_KEY = (import.meta.env.VITE_CARTO_API_KEY as string | undefined)?.trim() || null;

export const BASEMAP_URL = CARTO_API_KEY
  ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`
  : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
export const BASEMAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

interface BasemapOption {
  id: string;
  /** Text key (not a literal) — resolved with useT() when shown in the LayersControl, see SubBasinMap.tsx. */
  nameKey: TextKey;
  url: string;
  attribution: string;
  /**
   * ALWAYS a real number, never `undefined` — passing `maxZoom={undefined}`
   * to a <TileLayer> (instead of omitting the prop entirely) makes
   * L.Util.setOptions() create its own `maxZoom: undefined` property that
   * HIDES Leaflet's default value (18), and TileLayer.initialize() does
   * `Math.max(minZoom, maxZoom)` without checking for that —
   * `Math.max(0, undefined)` gives `NaN`, and that NaN contaminates
   * `map._layersMaxZoom` as soon as that layer is active, leaving zoom
   * (wheel and +/- buttons) completely broken while it is. Real bug found
   * while building the layer selector: only "Terrain" (the only one that
   * did bring a numeric maxZoom) zoomed correctly.
   */
  maxZoom: number;
}

/**
 * Base map alternatives for the working map (Leaflet layers selector, see
 * SubBasinMap.tsx) — "satelite"/"terreno" are free with no API key needed;
 * "calles" (CARTO Voyager, BASEMAP_URL) needs VITE_CARTO_API_KEY set — see
 * that constant's own comment. Landing.tsx's decorative map still only uses
 * BASEMAP_URL/BASEMAP_ATTRIBUTION, no selector.
 */
export const BASEMAPS: BasemapOption[] = [
  {
    id: "calles",
    nameKey: "basemap.streets",
    url: BASEMAP_URL,
    attribution: BASEMAP_ATTRIBUTION,
    maxZoom: 20,
  },
  {
    id: "satelite",
    nameKey: "basemap.satellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, GIS User Community",
    maxZoom: 19,
  },
  {
    id: "terreno",
    nameKey: "basemap.terrain",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM — mapa: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxZoom: 17,
  },
];
